/**
 * Demographics Reference Data — Static arrays for mandatory variable dropdowns.
 *
 * Used by MappingReviewStep to render manual override controls when a mandatory
 * demographic variable has no mapped column (e.g., mono-country study).
 *
 * Kept in a separate file to allow lazy-loading and avoid adding ~10KB to the
 * main bundle for pages that don't need it.
 *
 * @see docs/mandatory-demographics-plan.md — Step 1.2
 */

import { COUNTRIES as SHARED_COUNTRIES } from '@digital-twins/shared-types'

/* -------------------------------------------------------------------------- */
/*  Country — ISO 3166-1 alpha-2                                               */
/* -------------------------------------------------------------------------- */

export { COUNTRIES } from '@digital-twins/shared-types'
export type { CountryEntry } from '@digital-twins/shared-types'

/** Set of valid ISO 3166-1 alpha-2 codes for backend validation. */
export const VALID_COUNTRY_CODES = new Set(SHARED_COUNTRIES.map((c) => c.code))

/* -------------------------------------------------------------------------- */
/*  Gender                                                                     */
/* -------------------------------------------------------------------------- */

export interface GenderOption {
  value: string
  label: string
}

/**
 * Gender options for manual override.
 * Values match the tokens recognized by `_extract_gender_from_value()` in personas.py.
 */
export const GENDER_OPTIONS: GenderOption[] = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Non-binary', label: 'Non-binary' },
  { value: 'Other', label: 'Other' },
]

/** Set of valid gender values for backend validation. */
export const VALID_GENDER_VALUES = new Set(GENDER_OPTIONS.map((g) => g.value))

/* -------------------------------------------------------------------------- */
/*  Age Ranges                                                                 */
/* -------------------------------------------------------------------------- */

export interface AgeRangeOption {
  value: string
  label: string
}

/**
 * Standard market research age brackets.
 * Values are stored as-is in constantContext and used directly by persona generation.
 */
export const AGE_RANGE_OPTIONS: AgeRangeOption[] = [
  { value: 'Under 18', label: 'Under 18' },
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25-34' },
  { value: '35-44', label: '35-44' },
  { value: '45-54', label: '45-54' },
  { value: '55-64', label: '55-64' },
  { value: '65+', label: '65+' },
]

/** Set of valid age range values for backend validation. */
export const VALID_AGE_RANGES = new Set(AGE_RANGE_OPTIONS.map((a) => a.value))
